import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, useForm } from "formik-redux";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import classnames from "classnames";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import usePrevious from "../../hooks/use-previous.hook";
import useTranslate from "../../hooks/use-translate.hook";
import { selectors as formSelectors, validateFields } from "../../form";
import { Dialog, cssUtils } from "../../ui";
import { savePerson } from "../customer.actions";
import { ADD_PERSON_FORM } from "../customer.constants";
import { getPersons } from "../customer.selectors";
import CustomerWizard from "../customer-wizard.component";
import css from "./person.scss";

const fieldsToInitialValues = fields => fields
    .map( field => field.id )
    .reduce( ( model, id ) => ({ ...model, [ id ]: null }), {} );

const AddPerson = ({ customer, opportunity }) => {
    const dispatch = useDispatch();

    const t = useTranslate();

    const fields = useSelector( formSelectors.list( "PERSON" ) );
    const persons = useSelector( getPersons );

    const filteredPersons = ( persons || [] ).filter( person => !opportunity || person.id !== opportunity.personId );

    const [ open, setOpen ] = useState( false );
    const toggleOpen = () => setOpen( !open );

    const initialValues = useMemo( () => ({
        id: customer.id,
        opportunityId: opportunity && opportunity.id,
        person: {
            fields: fieldsToInitialValues( fields )
        }
    }), [ customer, opportunity, fields ] );

    const formik = useForm({
        form: ADD_PERSON_FORM,
        enableReinitialize: true,
        initialValues,
        onSubmit: values => dispatch( savePerson( values ) ),
        validate: values => {
            const errors = {};
            const person = get( values, "person", {} );
            if ( person.new ) {
                errors.person = validateFields( person, { t, fields } );
            } else if ( !person.id ) {
                errors.person = {
                    id: "common:validation.selection"
                };
            }

            if ( errors.person && isEmpty( errors.person ) ) {
                delete errors.person;
            }

            return errors;
        },
    });

    const prevSubmitting = usePrevious( formik.submitting );

    const handleResetForm = () => formik.setFieldValue( "person.fields", initialValues.person.fields );

    useEffect( () => {
        if ( prevSubmitting && !formik.submitting ) {
            setOpen( false );
        }
    }, [ prevSubmitting, formik.submitting ] );

    return (
        <>
            <IconButton onClick={ toggleOpen } color="inherit" size="large">
                <AddIcon/>
            </IconButton>

            <Dialog open={ open } fullWidth maxWidth="sm">
                <DialogTitle className={ css.titleAddPerson }>
                    { t( "customer:person.add-title" ) }
                </DialogTitle>
                <DialogContent className={ classnames( cssUtils.paddingLeft0, cssUtils.paddingRight0 )}>
                    <Form formik={ formik }>
                        <CustomerWizard
                            name="person"
                            type="PERSON"
                            resetForm={ handleResetForm }
                            setFieldValue={ formik.setFieldValue }
                            suggestions={ opportunity && filteredPersons }
                            suggestionsTitle={ t( "customer:person.company-persons" ) }
                        />
                    </Form>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" disabled={ formik.submitting } onClick={ toggleOpen }>
                        { t( "common:cancel" ) }
                    </Button>
                    <Button color="primary" disabled={ formik.submitting } onClick={ formik.submitForm }>
                        { t( "common:save" ) }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddPerson;